import * as angular from "angular";
import * as moment from "moment";

import { IImportAdapterResult, ImportPreProcessResult, IRowCol } from "../interfaces/IImportAdapterResult";
import { IImportOption } from "../interfaces/IImportOption";
import { IImportSite } from "../interfaces/IImportSite";
import { IImportValidationError } from "../interfaces/IImportValidation";
import { IOptionIndexes } from "../interfaces/IOptionIndexes";
import { IParagonData, IParagonDataItem } from "../interfaces/IParagonData";

import { CsvImporterService } from "../csvImporter.service";
import { FieldType } from "../enums/FieldType";

export class ParagonImportAdapterService {

    static $inject = ["csvImporterService"];

    constructor(private readonly csvImporterService: CsvImporterService) {
    }

    preProcessImportFile(csvLines: string[], startOfWeekDate: Date): IImportAdapterResult {

        const errors = [] as IImportValidationError[];
        const importOptions = angular.copy(this.getImportOptions());

        if (!this.hasValidMinimumLines(csvLines, errors) || !this.setTitleOptions(csvLines, importOptions, errors)) {
            return this.importErrorResult(errors);
        }

        this.removeTitleLine(csvLines);

        if (!this.validatePreProcessingFields(csvLines, importOptions, errors)) {
            return this.importErrorResult(errors);
        }

        const paragonData = this.getParagonData(csvLines, importOptions, startOfWeekDate);

        return this.translateImportedData(paragonData);
    }

    fileHasParagonHeader(csvLines: string[]): boolean {
        const importOptions = angular.copy(this.getImportOptions());
        const titleRowCellValues = this.csvImporterService.getTitleRowCellValues(csvLines);
        const validationErrors = [] as IImportValidationError[];

        if (!titleRowCellValues || !importOptions || titleRowCellValues.length < 4 || importOptions.length < 4) {
            return false;
        }

        let paragonMatchCols = 0;
        for (let colIdx = 0; colIdx < 4; colIdx++) {
            const option = importOptions[colIdx];
            this.csvImporterService.mapImportOptionFromTitles(option, titleRowCellValues, validationErrors, true);
            if (option.isValid) {
                paragonMatchCols++;
            }
        }

        // tests that import file has matches for at least 3 of first 4 header columns
        return paragonMatchCols >= 3;
    }

    private importErrorResult(errors: IImportValidationError[]) {
        return {
            preProcessedDataRows: [],
            errors: errors,
            status: ImportPreProcessResult.Error
        };
    }

    private hasValidMinimumLines(csvLines: string[], errors: IImportValidationError[]): boolean {
        const titleLine = csvLines && csvLines.length ? csvLines[0] : null;
        if (!titleLine) {
            errors.push(this.getValidationError("None", "None", "No data found in import file", 0, "0"));
            return false;
        }

        if (csvLines.length < 2) {
            errors.push(this.getValidationError("None", "None", "Only header and no data found in import file", 1, "1"));
            return false;
        }

        return true;
    }

    private validatePreProcessingFields(csvLines: string[], importOptions: IImportOption[], errors: IImportValidationError[]): boolean {
        csvLines.forEach((csvLine, rowIdx) => {
            const rowCells = csvLine.split(",");
            importOptions.forEach(importOption => {
                const cellValue = rowCells[importOption.mappedColumnIdx];
                this.validateField(importOption, cellValue, rowIdx, importOption.mappedColumnIdx, errors);
            });
        });

        return errors.length === 0;
    }

    private validateField(option: IImportOption, cellValue: string, rowIdx: number, colIdx: number, errors: IImportValidationError[]): boolean {
        if (this.csvImporterService.isEmptyCell(cellValue)) {
            if (option.isRequired || option.isRequired === undefined) {
                errors.push(this.getValidationError(option.displayName, cellValue, "Field cannot be empty", rowIdx, colIdx.toString()));
            }
            return !option.isRequired;
        }

        const validationResult = this.csvImporterService.isValidFieldForType(cellValue, option.fieldType);
        if (!validationResult.isValid) {
            errors.push(this.getValidationError(option.displayName, cellValue, this.csvImporterService.getInvalidFieldForTypeMessage(option, validationResult.message), rowIdx, colIdx.toString()));
            return false;
        }

        if (option.validate && !option.validate(cellValue)) {
            const validationText = option.validationText || "Field is invalid";
            errors.push(this.getValidationError(option.displayName, cellValue, validationText, rowIdx, colIdx.toString()));
            return false;
        }

        return true;
    }

    private translateImportedData(paragonData: IParagonData): IImportAdapterResult {

        const translatedCsvRows = [];

        const originalRowCols: IRowCol[][] = [];

        paragonData.paragonDataItems.forEach(paragonDataItem => {
            const csvLine = this.getCsvLine(paragonDataItem);
            translatedCsvRows.push(csvLine);

            const rowCols = this.getOriginalRowCols(paragonData, paragonDataItem, translatedCsvRows.length - 1);
            originalRowCols.push(rowCols);
        });

        const result: IImportAdapterResult = {
            preProcessedDataRows: translatedCsvRows,
            originalRowCols: originalRowCols,
            errors: [],
            status: ImportPreProcessResult.Ok
        };

        return result;
    }

    private getOriginalRowCols(paragonData: IParagonData, dataItem: IParagonDataItem, rowIdx: number): IRowCol[] {
        const optionIndexes = paragonData.optionIndexes;
        const importOptions = paragonData.importOptions;

        const routeColsFrom: number[] = [
            importOptions[optionIndexes.vehicleReg].mappedColumnIdx,
            importOptions[optionIndexes.sourceDepotDepartureTime].mappedColumnIdx,
            importOptions[optionIndexes.sourceDepot].mappedColumnIdx,
            importOptions[optionIndexes.sourceDepotDepartureTime].mappedColumnIdx,
            importOptions[optionIndexes.sourceDepotDepartureTime].mappedColumnIdx,
            importOptions[optionIndexes.sourceDepotDepartureTime].mappedColumnIdx
        ];

        const rowCols = [] as IRowCol[];

        for (let colIdx = 0; colIdx < routeColsFrom.length; colIdx++) {
            rowCols.push({
                row: rowIdx,
                colFrom: routeColsFrom[colIdx] + 1
            });
        }

        const siteColsFrom: number[] = [
            importOptions[optionIndexes.siteName].mappedColumnIdx,
            importOptions[optionIndexes.siteArrivalTime].mappedColumnIdx,
            importOptions[optionIndexes.siteDepartTime].mappedColumnIdx,
            importOptions[optionIndexes.travelDistanceToNextCall].mappedColumnIdx
        ];

        for (let siteIdx = 1; siteIdx < dataItem.sites.length; siteIdx++) {
            const site = dataItem.sites[siteIdx];
            for (let siteColIdx = 0; siteColIdx < siteColsFrom.length; siteColIdx++) {
                rowCols.push({
                    row: site.sourceRow,
                    colFrom: siteColsFrom[siteColIdx] + 1
                });
            }
        }

        return rowCols;
    }

    private getCsvLine(dataItem: IParagonDataItem) {
        let csvLine = this.getDepotCsvString(dataItem);

        for (let i = 1; i < dataItem.sites.length; i++) {
            const site = dataItem.sites[i];

            csvLine += this.getSiteCsvString(site);
        }

        return csvLine;
    }

    private getDepotCsvString(dataItem: IParagonDataItem): string {
        return dataItem.vehicleReg +
            "," +
            this.toFormattedDate(dataItem.routeStartTime) +
            "," +
            dataItem.sites[0].siteName +
            "," +
            this.toFormattedDate(dataItem.sites[0].arrivalTime) +
            "," +
            this.toFormattedDate(dataItem.sites[0].departureTime) +
            "," +
            dataItem.sites[0].distance +
            ",";
    }

    private getSiteCsvString(site: IImportSite): string {
        return site.siteName +
            "," +
            this.toFormattedDate(site.arrivalTime) +
            "," +
            this.toFormattedDate(site.departureTime) +
            "," +
            site.distance +
            ",";
    }

    private toFormattedDate(dateMoment: moment.Moment) {
        return dateMoment.format("YYYY-MM-DDTHH:mm:ss") + "Z";
    }

    private setTitleOptions(csvLines: string[], importOptions: IImportOption[], validationErrors: IImportValidationError[]): boolean {
        const titleRowCellValues = this.getTitleRowCellValues(csvLines);

        importOptions.forEach(option => {
            this.csvImporterService.mapImportOptionFromTitles(option, titleRowCellValues, validationErrors);
        });

        return validationErrors.length === 0;
    }

    private removeTitleLine(csvLines: string[]) {
        csvLines.splice(0, 1);
    }

    private getTitleRowCellValues(csvLines: string[]): string[] {
        const titleRow = csvLines.length ? csvLines[0] : "";
        return this.csvImporterService.getFieldValuesFromLine(titleRow);
    }

    private getParagonData(dataRows: string[], importOptions: IImportOption[], startOfWeekDate: Date): IParagonData {

        const paragonData = this.initParagonData(startOfWeekDate, importOptions);

        dataRows.forEach((dataRow, rowIdx) => {

            const cellValues = this.csvImporterService.getFieldValuesFromLine(dataRow);

            this.importCellValues(cellValues, paragonData, rowIdx);
        });

        if (paragonData.paragonDataItem.sites.length) {
            this.storeRouteData(paragonData);
        }

        return paragonData;
    }

    private initParagonData(startOfWeekDate: Date, importOptions: IImportOption[]): IParagonData {
        const paragonDataItems = [] as IParagonDataItem[];
        const paragonDataItem = this.initParagonDataItem();
        const startOfWeekDateNoTime = this.getStartOfWeekDateNoTime(startOfWeekDate);

        return {
            paragonDataItems: paragonDataItems,
            paragonDataItem: paragonDataItem,
            endDepot: null,
            siteNextCallTravelDistance: 0,
            startOfWeekDate: startOfWeekDateNoTime,
            importOptions: importOptions,
            optionIndexes: this.getImportOptionIndexes()
        } as IParagonData;
    }

    private getStartOfWeekDateNoTime(startOfWeekDate: Date) {
        return new Date(startOfWeekDate.getFullYear(), startOfWeekDate.getMonth(), startOfWeekDate.getDate());
    }

    // ***************************************************** //
    // *************** IMPORT FUNCTION ********************* //
    // ***************************************************** //
    private importCellValues(cellValues: string[], paragonData: IParagonData, rowIdx: number) {

        if (this.isNewRouteTrip(cellValues, paragonData)) {
            if (paragonData.paragonDataItem.sites.length) {
                this.storeRouteData(paragonData);
            }
            this.setNextRoute(cellValues, paragonData, rowIdx);
        }

        if (this.isNextSite(cellValues, paragonData)) {
            this.setSite(cellValues, paragonData, rowIdx);
        } else {
            this.updateSite(cellValues, paragonData, rowIdx);
        }
    }

    private storeRouteData(paragonData: IParagonData) {
        this.storeEndDepot(paragonData);
        paragonData.paragonDataItems.push(paragonData.paragonDataItem);
        paragonData.paragonDataItem = this.initParagonDataItem();
    }

    private storeEndDepot(paragonData: IParagonData) {
        paragonData.endDepot.targetColFrom = this.getTargetCol(paragonData.paragonDataItem);
        paragonData.endDepot.distance = paragonData.siteNextCallTravelDistance;
        paragonData.paragonDataItem.sites.push(paragonData.endDepot);
    }

    private isNextSite(cellValues: string[], paragonData: IParagonData): boolean {
        return paragonData.paragonDataItem.sites.length < 2 || this.isNewCustomer(cellValues, paragonData);
    }

    private setSite(cellValues: string[], paragonData: IParagonData, rowIdx: number) {
        const nextSite = this.getSiteVisit(cellValues, rowIdx, paragonData);

        paragonData.paragonDataItem.sites.push(nextSite);
    }

    private updateSite(cellValues: string[], paragonData: IParagonData, rowIdx: number) {
        const latestSite = this.getSiteVisit(cellValues, rowIdx, paragonData);

        const lastSiteIdx = paragonData.paragonDataItem.sites.length - 1;
        const siteToUpdate = paragonData.paragonDataItem.sites[lastSiteIdx];

        siteToUpdate.distance += latestSite.distance;
        siteToUpdate.departureTime = this.getDepartureTime(cellValues, paragonData);
    }

    private initParagonDataItem() {
        const paragonDataItem: IParagonDataItem = {
            route: "",
            tripNo: 0,
            vehicleReg: "",
            routeStartTime: null,
            sites: [] as IImportSite[],
            isValid: true,
            errors: [] as IImportValidationError[]
        };
        return paragonDataItem;
    }

    private setNextRoute(cellValues: string[], paragonData: IParagonData, rowIdx: number): void {
        const paragonDataItem = paragonData.paragonDataItem;

        paragonDataItem.route = this.getRoute(cellValues, paragonData);
        paragonDataItem.tripNo = this.getTripNo(cellValues, paragonData);
        paragonDataItem.vehicleReg = this.getVehicleReg(cellValues, paragonData);
        paragonDataItem.routeStartTime = this.getRouteStartTime(cellValues, paragonData.startOfWeekDate, paragonData);
        paragonDataItem.sites.push(this.getSourceDepot(cellValues, paragonData, rowIdx, paragonData.startOfWeekDate));

        paragonData.endDepot = this.getEndDepot(cellValues, paragonData, rowIdx);

        paragonData.siteNextCallTravelDistance = 0;
    }

    private isNewRouteTrip(cellValues: string[], paragonData: IParagonData): boolean {
        return this.isNewTripNo(cellValues, paragonData) || this.isNewRoute(cellValues, paragonData);
    }

    private isNewRoute(cellValues: string[], paragonData: IParagonData): boolean {
        const route = this.getRoute(cellValues, paragonData);

        return route !== paragonData.paragonDataItem.route;
    }

    private isNewTripNo(cellValues: string[], paragonData: IParagonData): boolean {
        const tripNo = this.getTripNo(cellValues, paragonData);
      
        return tripNo !== paragonData.paragonDataItem.tripNo;
    }

    private isNewCustomer(cellValues: string[], paragonData: IParagonData): boolean {

        const lastSiteIdx = paragonData.paragonDataItem.sites.length - 1;

        const customer = this.getCustomer(cellValues, paragonData);
        const lastCustomer = paragonData.paragonDataItem.sites[lastSiteIdx].siteName;

        return customer !== lastCustomer;
    }

    private getCellValueByColumnIndex(optionIndex: number, cellValues: string[], paragonData: IParagonData) {
        const colIdx = paragonData.importOptions[optionIndex].mappedColumnIdx;
        return cellValues[colIdx] || "";
    }

    private getRoute(cellValues: string[], paragonData: IParagonData) {
        return this.getCellValueByColumnIndex(paragonData.optionIndexes.route, cellValues, paragonData);
    }

    private getTripNo(cellValues: string[], paragonData: IParagonData): number {
        const tripNo = this.getCellValueByColumnIndex(paragonData.optionIndexes.trip, cellValues, paragonData);
        return tripNo ? +tripNo : 0;
    }

    private getCustomer(cellValues: string[], paragonData: IParagonData) {
        return this.getCellValueByColumnIndex(paragonData.optionIndexes.siteName, cellValues, paragonData);
    }

    private getVehicleReg(cellValues: string[], paragonData: IParagonData) {
        return this.getCellValueByColumnIndex(paragonData.optionIndexes.vehicleReg, cellValues, paragonData);
    }

    private getRouteStartTime(cellValues: string[], startOfWeekDate: Date, paragonData: IParagonData): moment.Moment {
        const routeStartTime = this.getCellValueByColumnIndex(paragonData.optionIndexes.sourceDepotDepartureTime, cellValues, paragonData);

        return this.getDateTimeFromParagonDate(routeStartTime, startOfWeekDate);
    }

    private getSourceDepotDepartureTime(cellValues: string[], paragonData: IParagonData): moment.Moment {
        const sourceDepotDepartureTime = this.getCellValueByColumnIndex(paragonData.optionIndexes.sourceDepotDepartureTime, cellValues, paragonData);

        return this.getDateTimeFromParagonDate(sourceDepotDepartureTime, paragonData.startOfWeekDate);
    }

    private getSourceDepotName(cellValues: string[], paragonData: IParagonData) {
        return this.getCellValueByColumnIndex(paragonData.optionIndexes.sourceDepot, cellValues, paragonData);
    }

    private getEndDepotArrivalTime(cellValues: string[], paragonData: IParagonData): moment.Moment {
        const endDepotArrivalTime = this.getCellValueByColumnIndex(paragonData.optionIndexes.endDepotArrivalTime, cellValues, paragonData);

        return this.getDateTimeFromParagonDate(endDepotArrivalTime, paragonData.startOfWeekDate);
    }

    private getEndDepotName(cellValues: string[], paragonData: IParagonData) {
        return this.getCellValueByColumnIndex(paragonData.optionIndexes.endDepot, cellValues, paragonData);
    }

    private getSiteName(cellValues: string[], paragonData: IParagonData) {
        return this.getCellValueByColumnIndex(paragonData.optionIndexes.siteName, cellValues, paragonData);
    }

    private getSiteArrivalTime(cellValues: string[], paragonData: IParagonData): moment.Moment {
        const siteArrivalTime = this.getCellValueByColumnIndex(paragonData.optionIndexes.siteArrivalTime, cellValues, paragonData);

        return this.getDateTimeFromParagonDate(siteArrivalTime, paragonData.startOfWeekDate);
    }

    private getSiteDepartureTime(cellValues: string[], paragonData: IParagonData): moment.Moment {
        const siteDepartTime = this.getCellValueByColumnIndex(paragonData.optionIndexes.siteDepartTime, cellValues, paragonData);

        return this.getDateTimeFromParagonDate(siteDepartTime, paragonData.startOfWeekDate);
    }

    private getSourceDepot(cellValues: string[], paragonData: IParagonData, rowIdx: number, startOfWeekDate: Date): IImportSite {
        const sourceDepot = this.getSourceDepotName(cellValues, paragonData);

        // currently uses getSourceDepotDepartureTime since taken to be the same as the arrival time
        const sourceDepotArrivalTimeMoment = this.getSourceDepotDepartureTime(cellValues, paragonData);

        const departureTimeMoment = this.getSourceDepotDepartureTime(cellValues, paragonData);

        const siteImport: IImportSite = {
            siteName: sourceDepot ? sourceDepot : "",
            arrivalTime: sourceDepotArrivalTimeMoment,
            departureTime: departureTimeMoment,
            distance: 0,
            sourceRow: rowIdx + 1,
            sourceColFrom: paragonData.importOptions[paragonData.optionIndexes.sourceDepot].mappedColumnIdx + 1,
            targetColFrom: this.getTargetCol(paragonData.paragonDataItem)
        };

        return siteImport;
    }

    private getEndDepot(cellValues: string[], paragonData: IParagonData, rowIdx: number): IImportSite {
        const siteName = this.getEndDepotName(cellValues, paragonData);

        const endDepotArrivalTimeMoment = this.getEndDepotArrivalTime(cellValues, paragonData);

        const defaultEndDepotOnSiteMins = 0;   // this may need to be configurable?
        const endDepotDepartureTimeMoment = moment(endDepotArrivalTimeMoment.add(defaultEndDepotOnSiteMins, "m"));

        const endDepot: IImportSite = {
            siteName: siteName ? siteName : "",
            arrivalTime: endDepotArrivalTimeMoment,
            departureTime: endDepotDepartureTimeMoment,
            distance: 0,
            sourceRow: rowIdx + 1,
            sourceColFrom: paragonData.importOptions[paragonData.optionIndexes.endDepot].mappedColumnIdx + 1,
            targetColFrom: this.getTargetCol(paragonData.paragonDataItem)
        };

        return endDepot;
    }

    private getSiteVisit(cellValues: string[], rowIdx: number, paragonData: IParagonData): IImportSite {
        const siteName = this.getSiteName(cellValues, paragonData);
        const travelDistance = this.getTravelDistance(cellValues, paragonData);
        const arrivalTimeMoment = this.getArrivalTime(cellValues, paragonData);
        const departureTimeMoment = this.getDepartureTime(cellValues, paragonData);

        const siteImport: IImportSite = {
            siteName: siteName,
            arrivalTime: arrivalTimeMoment,
            departureTime: departureTimeMoment,
            distance: +travelDistance,
            sourceRow: rowIdx + 1,
            sourceColFrom: paragonData.importOptions[paragonData.optionIndexes.siteName].mappedColumnIdx + 1,
            targetColFrom: this.getTargetCol(paragonData.paragonDataItem) 
        };

        return siteImport;
    }

    private getTargetCol(paragonDataItem: IParagonDataItem): number {
        const siteOffsetCols = 2;
        const siteCols = 4;
        return siteOffsetCols + (paragonDataItem.sites.length * siteCols);
    }

    private getTravelDistance(cellValues: string[], paragonData: IParagonData): number {

        paragonData.siteNextCallTravelDistance = this.getNextCallTravelDistance(cellValues, paragonData);

        const travelDistanceStr = this.getTravelDistanceFromPrevCall(cellValues, paragonData);

        const travelDistance = travelDistanceStr ? +travelDistanceStr : 0;

        return travelDistance;
    }

    private getNextCallTravelDistance(cellValues: string[], paragonData: IParagonData): number {
        const colIdx = paragonData.importOptions[paragonData.optionIndexes.travelDistanceToNextCall].mappedColumnIdx;
        const travelDistanceStr = cellValues[colIdx];
        return travelDistanceStr ? (+travelDistanceStr * 1000) : 0;
    }

    private getTravelDistanceFromPrevCall(cellValues: string[], paragonData: IParagonData): number {
        const colIdx = paragonData.importOptions[paragonData.optionIndexes.travelDistanceFromPrevCall].mappedColumnIdx;
        const travelDistanceStr = cellValues[colIdx];

        return travelDistanceStr ? (+travelDistanceStr * 1000) : 0;
    }

    private getArrivalTime(cellValues: string[], paragonData: IParagonData): moment.Moment {
        const arrivalTimeMoment = this.getSiteArrivalTime(cellValues, paragonData);

        return arrivalTimeMoment;
    }

    private getDepartureTime(cellValues: string[], paragonData: IParagonData): moment.Moment {
        const departureTimeMoment = this.getSiteDepartureTime(cellValues, paragonData);

        return departureTimeMoment;
    }

    // converts paragon day time (e.g. "08:42 Mon") to moment
    private getDateTimeFromParagonDate(paragonDate, startOfWeekDate: Date): moment.Moment {
        const paragonDateFields = paragonDate.split(" ");

        const time = paragonDateFields[0].split(":");
        const hours = time[0];
        const mins = time.length > 1 ? time[1] : "0";
        const day = paragonDateFields.length > 1 ? paragonDateFields[1] : "";

        const dayOfWeekDate = moment(startOfWeekDate, "YYYY-MM-DD").day(day + "day");
        const dateTimeResult = dayOfWeekDate.add(+hours, "h").add(+mins, "m");

        return dateTimeResult;
    }

    isValidTime(paragonTime): boolean {
        const paragonDateFields = (paragonTime || "").split(" ");
        if (paragonDateFields.length < 2) {
            return false;
        }
        const time = paragonTime[0].split(":");
        const hours = time[0];
        if (!this.csvImporterService.isValidInteger(hours)) {
            return false;
        }
        const hour = +hours;
        if (hour < 0 || hour > 23) {
            return false;
        }
        const mins = time.length > 1 ? time[1] : "0";
        if (!this.csvImporterService.isValidInteger(mins)) {
            return false;
        }
        const min = +mins;
        if (min < 0 || hour > 59) {
            return false;
        }
        const day = paragonDateFields[1].toLowerCase();

        return this.isValidDay(day);
    }

    private isValidDay(day: string): boolean {
        switch (day) {
            case "sun":
            case "mon":
            case "tue":
            case "wed":
            case "thu":
            case "fri":
            case "sat":
                return true;
            default:
                return false;
        }
    }

    private getValidationError(fieldName: string, fieldValue: string, validationText: string, rowIndex: number, colIndex?: string): IImportValidationError {
        const error: IImportValidationError = {
            rowNumber: rowIndex + 1,
            colNumber: colIndex,
            fieldName: fieldName,
            fieldValue: fieldValue,
            text: validationText
        };

        return error;
    }

    private getImportOptionIndexes(): IOptionIndexes {
        const optionIndexes: IOptionIndexes = {
            route: 0,
            trip: 1,
            sourceDepot: 2,
            sourceDepotDepartureTime: 3,
            endDepot: 4,
            endDepotArrivalTime: 5,
            siteName: 6,
            siteArrivalTime: 7,
            siteDepartTime: 8,
            vehicleReg: 9,
            travelDistanceFromPrevCall: 10,
            travelDistanceToNextCall: 11
        }

        return optionIndexes;
    }

    private getImportOptions(): IImportOption[] {

        const csvImportOptions: IImportOption[] = [
            {
                columnName: "RouteNo",
                aliases: ["route", "route num", "route number"],
                displayName: "Route Number",
                fieldType: FieldType.Integer
            },
            {
                columnName: "TripNo",
                aliases: ["trip", "trip num", "trip number"],
                displayName: "Trip Number",
                fieldType: FieldType.Integer
            },
            {
                columnName: "Source Depot",
                aliases: ["sourceDepotName"],
                displayName: "Source Depot",
                fieldType: FieldType.String
            },
            {
                columnName: "Source Depot Departure Time",
                aliases: ["sourceDepotDepartureDateTime"],
                displayName: "Source Depot Departure Time",
                fieldType: FieldType.String,
                validate: (val) => { return this.isValidTime(val); },
                validationText: "Field not in valid format (expected format: HH:Mi DDD eg. 08:42 Thu)"
            },
            {
                columnName: "End Depot",
                aliases: ["endDepotName"],
                displayName: "End Depot",
                fieldType: FieldType.String
            },
            {
                columnName: "End Depot Arrival Time",
                aliases: ["endDepotArrivalDateTime"],
                displayName: "End Depot Arrival Time",
                fieldType: FieldType.String,
                validate: (val) => { return this.isValidTime(val); },
                validationText: "Field not in valid format (expected format: HH:Mi DDD eg. 11:12 Thu)"
            },
            {
                columnName: "Customer Name",
                aliases: ["customer"],
                displayName: "Customer Name",
                fieldType: FieldType.String
            },
            {
                columnName: "Arrival Time",
                aliases: ["arrivalDateTime"],
                displayName: "Arrival Time",
                fieldType: FieldType.String,
                validate: (val) => { return this.isValidTime(val); },
                validationText: "Field not in valid format (expected format: HH:Mi DDD eg. 10:12 Thu)"
            },
            {
                columnName: "Depart Time",
                aliases: ["departureTime", "departDateTime", "departureDateTime"],
                displayName: "Depart Time",
                fieldType: FieldType.String,
                validate: (val) => { return this.isValidTime(val); },
                validationText: "Field not in valid format (expected format: HH:Mi DDD eg. 10:22 Thu)"
            },
            {
                columnName: "RigidGroupName",
                aliases: ["rigidGroup", "rigidName", "groupName"],
                displayName: "Rigid Group Name",
                fieldType: FieldType.String
            },
            {
                columnName: "TravelDistanceFromPrevCall",
                aliases: ["travelDistanceFromPreviousCall", "distanceFromPreviousCall", "distanceFromPrevCall", "distancePreviousCall", "distancePrevCall"],
                displayName: "Travel Distance From Previous Call",
                fieldType: FieldType.PositiveDouble
            },
            {
                columnName: "TravelDistanceToNextCall",
                aliases: ["travelDistanceNextCall", "distanceToNextCall", "distanceNextCall"],
                displayName: "Travel Distance From Previous Call",
                fieldType: FieldType.PositiveDouble
            }
        ];

        return csvImportOptions;
    }
}
